import { useEffect, useRef, useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { PageHeader } from '../components/ui.jsx'
import { MailIcon, CloudIcon, CopyIcon, CheckIcon, CloseIcon } from '../components/icons.jsx'
import { useAuth, initialsOfUser } from '../auth.jsx'
import { NAME_MAX } from '../lib/cognito.js'
import { gmailStatus, gmailAuthUrl, gmailDisconnect } from '../lib/gmail.js'
import { hubspotStatus, syncAge, formatDayTime } from '../lib/hubspot.js'
import { useRbac } from '../lib/rbac.js'
import {
  cleanSignature,
  currentSignature,
  defaultSignature,
  hasSavedSignature,
  loadSignature,
  saveSignature,
  SIGNATURE_MAX,
} from '../lib/signature.js'
// The daily briefing's "leave these off" list: our own staff and test
// accounts, by address or domain. Per-browser, per-user (lib/taskExclusions.js)
// — the run reads it fresh each time, so a change here lands on the next
// /follow-up without a reload.
import {
  DEFAULT_EXCLUSIONS,
  addExclusion,
  normalizeExclusion,
  readExclusions,
  removeExclusion,
  resetExclusions,
  subscribeExclusions,
} from '../lib/taskExclusions.js'

// The mirror is one-way, so this card is a readout and a setup checklist — it
// has no connect button and, deliberately, no token field. The private-app
// token and client secret are pasted straight into Secrets Manager by an admin
// and never travel through the browser, so there is nothing here to submit and
// no secret to render.
const HS_CODE =
  'rounded-[4px] border border-line bg-surface px-[5px] py-px text-[11.5px] break-all'

const HS_TILES = [
  ['Companies', (o) => o.company],
  ['Contacts', (o) => o.contact],
  ['Deals', (o) => o.deal],
  ['Tasks', (o) => o.task],
  ['Engagements', (o) => o.note + o.email + o.call + o.meeting],
  ['Owners', (o) => o.owner],
]

const hsCount = (objects, key) => Number(objects?.[key]?.count || 0)

export default function Settings() {
  const { can, roleLabel } = useRbac()
  const { user, logout, isCognito, updateName } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [gmail, setGmail] = useState(null) // null = loading
  const [hubspot, setHubspot] = useState(null) // null = loading
  const [copied, setCopied] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  // Profile name editor. Seeded from the session and re-seeded whenever it
  // changes underneath us (sign-in, another tab, a save that came back
  // normalized by Cognito).
  const [firstName, setFirstName] = useState(user?.firstName || '')
  const [lastName, setLastName] = useState(user?.lastName || '')
  const [savingName, setSavingName] = useState(false)
  const [nameError, setNameError] = useState('')
  const [nameSaved, setNameSaved] = useState('')

  useEffect(() => {
    setFirstName(user?.firstName || '')
    setLastName(user?.lastName || '')
  }, [user?.firstName, user?.lastName])

  const nameDirty =
    firstName.trim() !== (user?.firstName || '') || lastName.trim() !== (user?.lastName || '')

  function editName(setter) {
    return (e) => {
      setNameError('')
      setNameSaved('')
      setter(e.target.value)
    }
  }

  async function handleSaveName(e) {
    e.preventDefault()
    setNameError('')
    setNameSaved('')
    setSavingName(true)
    try {
      const saved = await updateName(firstName, lastName)
      // Show what actually landed, not what was typed.
      setFirstName(saved.firstName || '')
      setLastName(saved.lastName || '')
      setNameSaved(
        isCognito
          ? 'Saved to your account — it follows you to any device.'
          : 'Saved to this browser (demo session — no account to write to).',
      )
    } catch (err) {
      setNameError(err.message || 'Could not save your name.')
    } finally {
      setSavingName(false)
    }
  }

  // ── the email signature ───────────────────────────────────────────────────
  // Seeded from what applies right now — the saved signature, else the profile
  // default (name · Revailo · email) — then refreshed from the account once.
  // Saved to the account; the cards append it under "Best," at render/copy
  // time (lib/signature.js), so a save here changes every draft at once.
  const [signature, setSignature] = useState(() => currentSignature(user))
  const [signatureBase, setSignatureBase] = useState(() => currentSignature(user))
  const [savingSig, setSavingSig] = useState(false)
  const [sigError, setSigError] = useState('')
  const [sigSaved, setSigSaved] = useState('')
  const [sigIsDefault, setSigIsDefault] = useState(() => !hasSavedSignature())
  const sigDirty = cleanSignature(signature) !== cleanSignature(signatureBase)
  const sigDirtyRef = useRef(sigDirty)
  sigDirtyRef.current = sigDirty

  useEffect(() => {
    let alive = true
    loadSignature({ force: true }).then(() => {
      if (!alive) return
      const now = currentSignature(user)
      // Don't clobber a signature the rep has already started typing.
      if (!sigDirtyRef.current) setSignature(now)
      setSignatureBase(now)
      setSigIsDefault(!hasSavedSignature())
    })
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email])

  async function handleSaveSignature(e) {
    e.preventDefault()
    setSigError('')
    setSigSaved('')
    setSavingSig(true)
    try {
      const res = await saveSignature(signature)
      const applied = res.signature || defaultSignature(user)
      setSignature(applied)
      setSignatureBase(applied)
      setSigIsDefault(!res.signature)
      setSigSaved(
        res.saved === 'account'
          ? res.signature
            ? 'Saved to your account — every draft now signs with it.'
            : 'Cleared — drafts sign with your profile default.'
          : 'Saved to this browser (the account could not be reached).',
      )
    } catch (err) {
      setSigError(err.message || 'Could not save your signature.')
    } finally {
      setSavingSig(false)
    }
  }

  // ── daily-task exclusions ─────────────────────────────────────────────────
  // Seeded from the store and kept live through its subscription, so a write
  // from anywhere (this form, another component, a reset) repaints the chips
  // without this page owning a second copy of the list that could drift.
  const [exclusions, setExclusions] = useState(() => readExclusions())
  const [exclusionInput, setExclusionInput] = useState('')
  const [exclusionError, setExclusionError] = useState('')
  useEffect(() => subscribeExclusions(() => setExclusions(readExclusions())), [])

  function handleAddExclusion(e) {
    // A form so Enter adds — but never a page submit, which would drop the
    // whole Settings state (and the half-typed signature above) on the floor.
    e.preventDefault()
    const value = normalizeExclusion(exclusionInput)
    if (!value) {
      // Refused, not silently dropped: the rep must see WHY nothing appeared,
      // or "revailo" (no dot) looks like it was added and the staff tasks
      // keep showing tomorrow.
      const typed = exclusionInput.trim()
      setExclusionError(
        typed
          ? `“${typed}” is not an email address or a domain — enter one like jane@revailo.com or revailo.com.`
          : 'Enter an email address or a domain, like jane@revailo.com or revailo.com.',
      )
      return
    }
    if (exclusions.entries.includes(value)) {
      setExclusionError(`${value} is already on the list.`)
      return
    }
    addExclusion(value)
    setExclusions(readExclusions())
    setExclusionInput('')
    setExclusionError('')
  }

  function handleRemoveExclusion(entry) {
    removeExclusion(entry)
    setExclusions(readExclusions())
    setExclusionError('')
  }

  function handleResetExclusions() {
    resetExclusions()
    setExclusions(readExclusions())
    setExclusionInput('')
    setExclusionError('')
  }

  // Returning from the Google consent screen lands here with ?gmail=…
  useEffect(() => {
    const result = searchParams.get('gmail')
    if (!result) return
    if (result === 'connected') setNotice('Gmail connected — the daily worklist can now read your inbox and catch prospects waiting on a reply.')
    else setError(`Gmail connection failed (${searchParams.get('reason') || 'unknown error'}). Try again.`)
    setSearchParams({}, { replace: true })
  }, [searchParams, setSearchParams])

  useEffect(() => {
    let cancelled = false
    if (user?.email) {
      gmailStatus().then((s) => {
        if (!cancelled) setGmail(s)
      })
    }
    return () => {
      cancelled = true
    }
  }, [user, notice])

  // Status only — there is nothing to connect from here, so this never runs on
  // a user action. An unreachable mirror resolves to an honest offline state
  // rather than leaving the card spinning.
  useEffect(() => {
    let cancelled = false
    if (!user?.email) return undefined
    Promise.resolve()
      .then(hubspotStatus)
      .catch(() => ({ available: false, configured: false, connected: false }))
      .then((s) => {
        if (!cancelled) setHubspot(s || { available: false })
      })
    return () => {
      cancelled = true
    }
  }, [user])

  const webhookUrl =
    hubspot?.webhook_url ||
    `${typeof window === 'undefined' ? '' : window.location.origin}/api/hubspot/webhook`

  async function copyWebhook() {
    try {
      await navigator.clipboard.writeText(webhookUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard blocked (insecure origin, denied permission) — the URL is
      // right there in the page to select by hand.
    }
  }

  async function handleConnect() {
    setError('')
    setBusy(true)
    try {
      window.location.href = await gmailAuthUrl()
    } catch (err) {
      setError(err.message)
      setBusy(false)
    }
  }

  async function handleDisconnect() {
    if (!window.confirm('Disconnect Gmail? The stored access is revoked.')) return
    setBusy(true)
    setError('')
    try {
      await gmailDisconnect()
      setNotice('Gmail disconnected.')
      setGmail(await gmailStatus())
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  const hsAge = syncAge(hubspot?.last_sync_at)
  const hsState =
    hubspot === null
      ? 'loading'
      : !hubspot.available
        ? 'unavailable'
        : !hubspot.configured
          ? 'not_configured'
          : !hubspot.connected
            ? 'not_connected'
            : hubspot.syncing
              ? 'syncing'
              : 'connected'
  const hsSummary = {
    loading: 'Checking status…',
    unavailable: 'The HubSpot API is not reachable from this environment.',
    not_configured: 'Not set up for this deployment yet.',
    not_connected: hubspot?.reason || 'Configured, but no sync has landed yet.',
    syncing: 'Connected — first backfill still running.',
    connected: `Connected — ${hsAge.label}`,
  }[hsState]
  const hsCounts = Object.fromEntries(
    ['company', 'contact', 'deal', 'task', 'note', 'email', 'call', 'meeting', 'owner'].map(
      (key) => [key, hsCount(hubspot?.objects, key)],
    ),
  )

  return (
    <>
      <PageHeader title="Settings" subtitle="Your account, workspace, and integrations." />

      <div className="grid max-w-[760px] gap-5">
        <section className="rounded-[12px] border border-line bg-surface p-5 shadow-card">
          <h3 className="m-0 mb-[2px] text-[14px] font-semibold text-ink">Profile</h3>
          <p className="m-0 mb-4 text-[12.5px] text-dim">
            Your signed-in identity. Your name is what teammates see on anything you author.
          </p>
          <div className="flex items-center gap-x-3" style={{ marginBottom: 16 }}>
            <span className="grid h-[34px] w-[34px] flex-none place-items-center rounded-[11px] bg-[#18181b] text-[12px] font-semibold tracking-[0.02em] text-white">{initialsOfUser(user)}</span>
            <div className="min-w-0">
              <div className="break-words font-semibold">{user?.name}</div>
              <div className="mt-[2px] break-all text-[12px] text-faint">{user?.email}</div>
            </div>
          </div>

          <form onSubmit={handleSaveName} className="mb-1 [border-top:1px_solid_var(--border)] pt-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-y-1.5 text-[13px] font-medium text-ink">
                First name
                <input
                  className="rounded-[10px] border border-line bg-panel px-[14px] py-[11px] text-[14px] font-normal text-ink placeholder:text-faint focus:border-primary focus:shadow-[0_0_0_3px_rgba(60,135,247,0.2)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                  value={firstName}
                  onChange={editName(setFirstName)}
                  placeholder="Jane"
                  autoComplete="given-name"
                  maxLength={NAME_MAX}
                  disabled={savingName}
                />
              </label>
              <label className="flex flex-col gap-y-1.5 text-[13px] font-medium text-ink">
                Last name
                <input
                  className="rounded-[10px] border border-line bg-panel px-[14px] py-[11px] text-[14px] font-normal text-ink placeholder:text-faint focus:border-primary focus:shadow-[0_0_0_3px_rgba(60,135,247,0.2)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                  value={lastName}
                  onChange={editName(setLastName)}
                  placeholder="Doe"
                  autoComplete="family-name"
                  maxLength={NAME_MAX}
                  disabled={savingName}
                />
              </label>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-x-1.5 rounded-[9px] bg-primary px-[14px] py-[7px] text-[13px] font-semibold text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-60"
                disabled={savingName || !nameDirty}
              >
                {savingName ? 'Saving…' : 'Save name'}
              </button>
              {nameDirty && !savingName && (
                <button
                  type="button"
                  className="inline-flex items-center gap-x-1.5 rounded-[8px] border border-line bg-transparent px-[14px] py-[7px] text-[13px] font-semibold text-dim transition-colors hover:bg-panel hover:text-ink"
                  onClick={() => {
                    setNameError('')
                    setNameSaved('')
                    setFirstName(user?.firstName || '')
                    setLastName(user?.lastName || '')
                  }}
                >
                  Cancel
                </button>
              )}
              {nameSaved && (
                <span className="text-[13px] font-semibold text-[#15803d] dark:text-[#4ade80]" role="status">
                  ✓ {nameSaved}
                </span>
              )}
            </div>

            {nameError && (
              <div className="mt-3 rounded-[8px] px-3 py-[9px] text-[13px] leading-[1.5] text-[#b91c1c] [border:1px_solid_#fecaca] bg-[#fef2f2] dark:bg-[rgba(220,38,38,0.15)] dark:text-[#fca5a5] dark:[border-color:rgba(220,38,38,0.4)]" role="alert">
                {nameError}
              </div>
            )}

            <p className="m-0 mt-3 text-[12px] leading-[1.5] text-faint">
              {isCognito
                ? 'Stored on your account, so it follows you to any device. Leave a field blank to unset it — we fall back to the part of your email before the @.'
                : 'Demo session: there is no account to write to, so this name lives in this browser only.'}
            </p>
          </form>

          <div className="flex justify-between gap-x-4 border-t border-line py-[10px] text-[13.5px] first-of-type:border-t-0">
            <span className="text-dim">Role</span>
            <span>{roleLabel}</span>
          </div>
          <div className="flex justify-between gap-x-4 border-t border-line py-[10px] text-[13.5px] first-of-type:border-t-0">
            <span className="text-dim">Sign-in</span>
            <span>{isCognito ? 'AWS Cognito' : 'Demo session (local dev)'}</span>
          </div>
          <div className="flex justify-between gap-x-4 border-t border-line py-[10px] text-[13.5px] first-of-type:border-t-0">
            <span className="text-dim">Session</span>
            <button
              className="inline-flex items-center gap-x-1.5 rounded-[8px] border border-line bg-transparent px-[14px] py-[7px] text-[13px] font-semibold text-dim transition-colors hover:bg-panel hover:text-ink"
              onClick={logout}
            >
              Sign out
            </button>
          </div>
        </section>

        <section className="rounded-[12px] border border-line bg-surface p-5 shadow-card">
          <h3 className="m-0 mb-[2px] text-[14px] font-semibold text-ink">Email signature</h3>
          <p className="m-0 mb-4 text-[12.5px] text-dim">
            Appended under “Best,” on every outreach draft you copy — the chat receipts, follow-ups, all of
            them. Starts from your profile; edit it however you sign. HubSpot's composer inserts its own
            signature too, so turn that off in HubSpot (Settings › General › Email) or the pasted email
            ends with both.
          </p>
          <form onSubmit={handleSaveSignature}>
            <label className="flex flex-col gap-y-1.5 text-[13px] font-medium text-ink">
              Signature
              <textarea
                className="min-h-[112px] resize-y rounded-[10px] border border-line bg-panel px-[14px] py-[11px] text-[14px] font-normal leading-[1.5] text-ink placeholder:text-faint focus:border-primary focus:shadow-[0_0_0_3px_rgba(60,135,247,0.2)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                value={signature}
                onChange={(e) => {
                  setSigError('')
                  setSigSaved('')
                  setSignature(e.target.value)
                }}
                placeholder={defaultSignature(user)}
                maxLength={SIGNATURE_MAX}
                rows={4}
                disabled={savingSig}
                spellCheck={false}
              />
            </label>
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-x-1.5 rounded-[9px] bg-primary px-[14px] py-[7px] text-[13px] font-semibold text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-60"
                disabled={savingSig || !sigDirty}
              >
                {savingSig ? 'Saving…' : 'Save signature'}
              </button>
              {cleanSignature(signature) !== defaultSignature(user) && !savingSig && (
                <button
                  type="button"
                  className="inline-flex items-center gap-x-1.5 rounded-[8px] border border-line bg-transparent px-[14px] py-[7px] text-[13px] font-semibold text-dim transition-colors hover:bg-panel hover:text-ink"
                  onClick={() => {
                    setSigError('')
                    setSigSaved('')
                    setSignature(defaultSignature(user))
                  }}
                  title="Rebuild the signature from your profile: name, workspace, email"
                >
                  Use profile default
                </button>
              )}
              {sigDirty && !savingSig && (
                <button
                  type="button"
                  className="inline-flex items-center gap-x-1.5 rounded-[8px] border border-line bg-transparent px-[14px] py-[7px] text-[13px] font-semibold text-dim transition-colors hover:bg-panel hover:text-ink"
                  onClick={() => {
                    setSigError('')
                    setSigSaved('')
                    setSignature(signatureBase)
                  }}
                >
                  Cancel
                </button>
              )}
              {sigSaved && (
                <span className="text-[13px] font-semibold text-[#15803d] dark:text-[#4ade80]" role="status">
                  ✓ {sigSaved}
                </span>
              )}
            </div>
            {sigError && (
              <div className="mt-3 rounded-[8px] px-3 py-[9px] text-[13px] leading-[1.5] text-[#b91c1c] [border:1px_solid_#fecaca] bg-[#fef2f2] dark:bg-[rgba(220,38,38,0.15)] dark:text-[#fca5a5] dark:[border-color:rgba(220,38,38,0.4)]" role="alert">
                {sigError}
              </div>
            )}
            <p className="m-0 mt-3 text-[12px] leading-[1.5] text-faint">
              {sigIsDefault
                ? 'No signature saved yet — drafts use this profile default (name · Revailo · email) until you save one.'
                : isCognito
                  ? 'Stored on your account, so it follows you to any device. Clear the box and save to go back to the profile default.'
                  : 'Demo session: saved in this browser only.'}
            </p>
          </form>
        </section>

        <section className="rounded-[12px] border border-line bg-surface p-5 shadow-card">
          <h3 className="m-0 mb-[2px] text-[14px] font-semibold text-ink">Workspace</h3>
          <p className="m-0 mb-4 text-[12.5px] text-dim">Revailo Sales workspace details.</p>
          <div className="flex justify-between gap-x-4 border-t border-line py-[10px] text-[13.5px] first-of-type:border-t-0">
            <span className="text-dim">Workspace</span>
            <span>Revailo Sales</span>
          </div>
          <div className="flex justify-between gap-x-4 border-t border-line py-[10px] text-[13.5px] first-of-type:border-t-0">
            <span className="text-dim">Team</span>
            <Link className="font-semibold text-primary-dark no-underline hover:underline" to="/collaboration">
              Manage members →
            </Link>
          </div>
        </section>

        {/* Who the daily briefing leaves off its lists. Not gated on a
            permission: every role that can run /daily-tasks or /follow-up may decide what
            its own browser hides, and the run says how many it hid. */}
        <section className="rounded-[12px] border border-line bg-surface p-5 shadow-card">
          <h3 className="m-0 mb-[2px] text-[14px] font-semibold text-ink">Daily Tasks &amp; Follow-Up</h3>
          <p className="m-0 mb-4 text-[12.5px] text-dim">
            Email addresses and domains both runs leave off their lists — our own staff and test
            accounts, so a HubSpot task in Daily Tasks or a trial in Follow-Up at one of these
            never reads as a customer to chase. Each run still says how many it hid.
          </p>

          {exclusions.entries.length ? (
            <ul className="m-0 mb-3 flex list-none flex-wrap gap-2 p-0">
              {exclusions.entries.map((entry) => (
                <li
                  key={entry}
                  className="inline-flex items-center gap-x-1 rounded-full border border-line bg-panel py-[3px] pl-[10px] pr-[4px] text-[12.5px] font-semibold text-dim"
                >
                  <span className="break-all">{entry}</span>
                  <button
                    type="button"
                    className="grid h-5 w-5 place-items-center rounded-full text-faint transition-colors hover:bg-[#fef2f2] hover:text-[#dc2626] dark:hover:bg-[rgba(220,38,38,0.16)] [&_svg]:h-[12px] [&_svg]:w-[12px]"
                    onClick={() => handleRemoveExclusion(entry)}
                    aria-label={`Remove ${entry}`}
                  >
                    <CloseIcon />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            // An emptied list is a choice the rep made, and it has a
            // consequence worth stating: the staff tasks come back.
            <p className="m-0 mb-3 text-[12.5px] text-dim">
              Nothing excluded — every task shows, including ones at our own domains.
            </p>
          )}

          <form onSubmit={handleAddExclusion}>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <label className="flex min-w-0 flex-1 flex-col gap-y-1.5 text-[13px] font-medium text-ink">
                <span className="sr-only">Email address or domain to exclude</span>
                <input
                  className="rounded-[10px] border border-line bg-panel px-[14px] py-[11px] text-[14px] font-normal text-ink placeholder:text-faint focus:border-primary focus:shadow-[0_0_0_3px_rgba(60,135,247,0.2)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                  value={exclusionInput}
                  onChange={(e) => {
                    setExclusionError('')
                    setExclusionInput(e.target.value)
                  }}
                  placeholder="jane@revailo.com or revailo.com"
                  autoComplete="off"
                  spellCheck={false}
                  inputMode="email"
                />
              </label>
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-x-1.5 rounded-[9px] bg-primary px-[14px] py-[7px] text-[13px] font-semibold text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-60"
                disabled={!exclusionInput.trim()}
              >
                Add
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-x-1.5 rounded-[8px] border border-line bg-transparent px-[14px] py-[7px] text-[13px] font-semibold text-dim transition-colors hover:bg-panel hover:text-ink disabled:cursor-not-allowed disabled:opacity-[0.45]"
                onClick={handleResetExclusions}
                disabled={exclusions.isDefault}
                title={
                  exclusions.isDefault
                    ? 'Already the default list'
                    : `Back to ${DEFAULT_EXCLUSIONS.join(' and ')}`
                }
              >
                Reset to defaults
              </button>
            </div>
            {exclusionError && (
              <div className="mt-3 rounded-[8px] px-3 py-[9px] text-[13px] leading-[1.5] text-[#b91c1c] [border:1px_solid_#fecaca] bg-[#fef2f2] dark:bg-[rgba(220,38,38,0.15)] dark:text-[#fca5a5] dark:[border-color:rgba(220,38,38,0.4)]" role="alert">
                {exclusionError}
              </div>
            )}
            <p className="m-0 mt-3 text-[12px] leading-[1.5] text-faint">
              By default {DEFAULT_EXCLUSIONS.join(' and ')} are excluded; a domain also covers its
              subdomains. This list is stored in this browser only — it does not follow you to
              another device.
            </p>
          </form>
        </section>

        {/* Personal Gmail hookup. Ungated on purpose: this is the signed-in
            user's OWN mailbox, not workspace data, and every role that works a
            lead needs it — drafts send from it and the daily worklist reads it
            to catch prospects waiting on a reply. */}
        <section className="rounded-[12px] border border-line bg-surface p-5 shadow-card">
          <h3 className="m-0 mb-[2px] text-[14px] font-semibold text-ink">Integrations</h3>
          <p className="m-0 mb-4 text-[12.5px] text-dim">
            Connect your own tools. Access is per-user and revocable any time.
          </p>

          <div className="mb-3 flex items-center gap-x-3">
            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[10px] bg-primary-tint text-primary-dark">
              <MailIcon />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[14px] font-semibold">Gmail</div>
              <div className="text-[12.5px] text-dim">
                {gmail === null
                  ? 'Checking status…'
                  : gmail.connected
                    ? `Connected as ${gmail.address}`
                    : 'Read your own inbox to track outreach replies. Sending goes through Apollo, not Gmail.'}
              </div>
            </div>
            {gmail?.connected ? (
              <button
                className="inline-flex items-center gap-x-1.5 rounded-[8px] border border-line bg-transparent px-[14px] py-[7px] text-[13px] font-semibold text-dim transition-colors hover:bg-panel hover:text-ink disabled:cursor-not-allowed disabled:opacity-[0.45]"
                onClick={handleDisconnect}
                disabled={busy}
              >
                Disconnect
              </button>
            ) : gmail?.configured ? (
              <button
                className="inline-flex items-center justify-center gap-x-1.5 rounded-[9px] bg-primary px-[14px] py-[7px] text-[13px] font-semibold text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-60"
                onClick={handleConnect}
                disabled={busy}
              >
                {busy ? 'Redirecting…' : 'Connect Gmail'}
              </button>
            ) : null}
          </div>

          {gmail?.connected && (
            <div className="flex justify-between gap-x-4 border-t border-line py-[10px] text-[13.5px] first-of-type:border-t-0">
              <span className="text-dim">Replies</span>
              <Link className="font-semibold text-primary-dark no-underline hover:underline" to="/chat">
                Work them in chat →
              </Link>
            </div>
          )}

          {gmail !== null && !gmail.connected && !gmail.configured && (
            <div className="rounded-[8px] border border-dashed border-line bg-panel px-[14px] py-3 text-[12.5px] leading-[1.5] text-dim">
              {gmail.available ? (
                <>
                  Gmail OAuth isn't configured for this deployment yet. An admin
                  creates an OAuth client in Google Cloud console (authorized
                  redirect URI{' '}
                  <code className="rounded-[4px] border border-line bg-surface px-[5px] py-px text-[11.5px]">https://revailo-sales.com/api/gmail/callback</code>) and
                  supplies <code className="rounded-[4px] border border-line bg-surface px-[5px] py-px text-[11.5px]">TF_VAR_google_oauth_client_id</code> /{' '}
                  <code className="rounded-[4px] border border-line bg-surface px-[5px] py-px text-[11.5px]">TF_VAR_google_oauth_client_secret</code> at the next{' '}
                  <code className="rounded-[4px] border border-line bg-surface px-[5px] py-px text-[11.5px]">terraform apply</code>. The connect button appears here
                  automatically once it's live.
                </>
              ) : (
                <>
                  The Gmail API isn't reachable from this environment (plain local
                  dev without <code className="rounded-[4px] border border-line bg-surface px-[5px] py-px text-[11.5px]">API_PROXY</code>). Run against the deployed API
                  to connect an inbox.
                </>
              )}
            </div>
          )}

          {error && (
            <div className="mt-3 rounded-[8px] border border-[#fecaca] bg-[#fef2f2] px-3 py-[9px] text-[13px] text-[#b91c1c] dark:border-[rgba(220,38,38,0.4)] dark:bg-[rgba(220,38,38,0.15)] dark:text-[#fca5a5]">
              {error}
            </div>
          )}
          {notice && (
            <div className="mb-3 mt-3 inline-block rounded-[10px] border border-[#bbf7d0] bg-[#f0fdf4] px-[14px] py-2 text-[13px] font-semibold text-[#15803d] dark:border-[rgba(34,197,94,0.35)] dark:bg-[rgba(34,197,94,0.12)] dark:text-[#4ade80]">
              ✓ {notice}
            </div>
          )}
        </section>

        {/* HubSpot mirror. Workspace-wide and read-only, so everyone can see
            the readout; only admins get the setup checklist. */}
        <section className="rounded-[12px] border border-line bg-surface p-5 shadow-card">
          <div className="mb-3 flex items-center gap-x-3">
            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[10px] bg-primary-tint text-primary-dark">
              <CloudIcon />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="text-[14px] font-semibold">HubSpot</span>
                <span className="rounded-[5px] border border-line bg-panel px-[6px] py-px text-[10.5px] font-semibold uppercase tracking-[0.04em] text-dim">
                  Read-only
                </span>
              </div>
              <div className="text-[12.5px] text-dim">{hsSummary}</div>
            </div>
            <span
              className={`h-[9px] w-[9px] flex-none rounded-full ${
                hsState === 'connected'
                  ? 'bg-[#16a34a]'
                  : hsState === 'syncing'
                    ? 'bg-[#d97706]'
                    : 'bg-[color:var(--border)]'
              }`}
              aria-hidden="true"
            />
          </div>

          <p className="m-0 mb-4 text-[12.5px] leading-[1.5] text-dim">
            Your portal is mirrored one way — HubSpot → Revailo Sales — and shown on each
            lead. Nothing is ever written back: the assistant can read this data and{' '}
            <em>propose</em> a change, but you make it in HubSpot yourself.
          </p>

          {(hsState === 'connected' || hsState === 'syncing') && (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {HS_TILES.map(([label, pick]) => (
                  <div key={label} className="rounded-[9px] border border-line bg-panel px-3 py-2">
                    <div className="text-[16px] font-semibold tabular-nums text-ink">
                      {pick(hsCounts).toLocaleString()}
                    </div>
                    <div className="text-[11.5px] text-faint">{label}</div>
                  </div>
                ))}
              </div>

              <div className="mt-3 flex justify-between gap-x-4 border-t border-line py-[10px] text-[13.5px]">
                <span className="text-dim">Last sync</span>
                <span className={hsAge.stale ? 'text-[#b45309] dark:text-[#fbbf24]' : ''}>
                  {hsAge.known ? formatDayTime(hubspot.last_sync_at) : 'unknown'}
                </span>
              </div>
              {hubspot.portal_id && (
                <div className="flex justify-between gap-x-4 border-t border-line py-[10px] text-[13.5px]">
                  <span className="text-dim">Portal</span>
                  <span className="tabular-nums">{hubspot.portal_id}</span>
                </div>
              )}
              {hsState === 'syncing' && (
                <div className="flex justify-between gap-x-4 border-t border-line py-[10px] text-[13.5px]">
                  <span className="text-dim">Backfill</span>
                  <span>
                    Running{hubspot.stale_pending ? ` — ${hubspot.stale_pending} queued` : ''}
                  </span>
                </div>
              )}
              {hubspot.last_error && (
                <div className="mt-3 rounded-[8px] border border-dashed border-line bg-panel px-[14px] py-3 text-[12.5px] leading-[1.5] text-dim">
                  Last sync error: {hubspot.last_error}
                  {hubspot.last_error_at ? ` (${formatDayTime(hubspot.last_error_at)})` : ''}. The
                  mirror keeps serving what it already has.
                </div>
              )}
            </>
          )}

          {hsState === 'unavailable' && (
            <div className="rounded-[8px] border border-dashed border-line bg-panel px-[14px] py-3 text-[12.5px] leading-[1.5] text-dim">
              The HubSpot API isn't reachable from this environment (plain local dev without{' '}
              <code className={HS_CODE}>API_PROXY</code>). Run against the deployed API to see the
              mirror.
            </div>
          )}

          {(hsState === 'not_configured' || hsState === 'not_connected') && (
            <div className="rounded-[8px] border border-dashed border-line bg-panel px-[14px] py-3 text-[12.5px] leading-[1.5] text-dim">
              {hubspot?.reason ||
                'HubSpot is not connected yet. The dashboard works exactly as it does without it — no HubSpot figures are shown or guessed at until a sync lands.'}
            </div>
          )}

          {can('integrations.manage') && (
            <div className="mt-4 border-t border-line pt-4">
              <h4 className="m-0 mb-[2px] text-[13px] font-semibold text-ink">Setup</h4>
              <p className="m-0 mb-3 text-[12.5px] leading-[1.5] text-dim">
                One-time, done by an admin. There is no token field on this page on purpose —
                credentials never pass through the browser.
              </p>
              <ol className="m-0 grid list-decimal gap-2 pl-[18px] text-[12.5px] leading-[1.6] text-dim">
                <li>
                  In HubSpot, create a classic private app (Settings → Integrations → Private
                  Apps) with the read scopes{' '}
                  <code className={HS_CODE}>crm.objects.companies.read</code>,{' '}
                  <code className={HS_CODE}>crm.objects.contacts.read</code>,{' '}
                  <code className={HS_CODE}>crm.objects.deals.read</code> and{' '}
                  <code className={HS_CODE}>crm.objects.owners.read</code>. No write scopes are
                  needed or used.
                </li>
                <li>
                  On the app's <strong>Auth</strong> tab, copy the access token (“Show token”) and
                  the client secret (“Show secret”) and paste them into the AWS Secrets Manager
                  secret <code className={HS_CODE}>revailo-sales/hubspot</code> as{' '}
                  <code className={HS_CODE}>access_token</code> and{' '}
                  <code className={HS_CODE}>client_secret</code>, replacing the{' '}
                  <code className={HS_CODE}>REPLACE_ME</code> placeholders. Terraform never sees
                  either value, and neither does this page.
                </li>
                <li>
                  On the app's <strong>Webhooks</strong> tab, set the target URL below, subscribe
                  to the object events you want mirrored, then <strong>Commit changes</strong>.
                  Turn on <strong>“Use expanded object support”</strong> if you want tasks, notes,
                  calls, emails and meetings to arrive in near-real time — without it those objects
                  are still mirrored, just on the slower polling pass.
                </li>
              </ol>

              <div className="mt-3">
                <div className="mb-1 text-[12px] font-medium text-dim">Webhook target URL</div>
                <div className="flex flex-wrap items-center gap-2">
                  <code className="min-w-0 flex-1 break-all rounded-[8px] border border-line bg-panel px-3 py-2 text-[12px] text-ink">
                    {webhookUrl}
                  </code>
                  <button
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-[8px] border border-line bg-transparent px-[14px] py-[7px] text-[13px] font-semibold text-dim transition-colors hover:bg-panel hover:text-ink"
                    onClick={copyWebhook}
                  >
                    {copied ? <CheckIcon /> : <CopyIcon />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <p className="m-0 mt-2 text-[12px] leading-[1.5] text-faint">
                  This must match <code className={HS_CODE}>hubspot_webhook_url</code> in
                  Terraform exactly — signature verification rebuilds the signed string from that
                  pinned value, never from the incoming request's host.
                </p>
              </div>

              <p className="m-0 mt-3 text-[12px] leading-[1.5] text-faint">
                HubSpot docs:{' '}
                <a
                  className="font-semibold text-primary-dark no-underline hover:underline"
                  href="https://developers.hubspot.com/docs/guides/apps/private-apps/overview"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  private apps
                </a>{' '}
                ·{' '}
                <a
                  className="font-semibold text-primary-dark no-underline hover:underline"
                  href="https://developers.hubspot.com/docs/guides/api/app-management/webhooks"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  webhooks
                </a>
              </p>
            </div>
          )}
        </section>
      </div>
    </>
  )
}
